import React, { Fragment, useCallback, useLayoutEffect, useRef, useState } from "react"

const responsive = (breakPoints) => {
  if (!breakPoints) return

  const sortedBreakPoints =
    breakPoints.sort((a, b) => {
      if (a.point >= b.point) return -1

      return 1
    })

  for (let breakPoint of sortedBreakPoints) {
    if (window.innerWidth >= breakPoint.point)
      return breakPoint
  }

  return breakPoints[breakPoints.length - 1]
}

const WindowVirtualGrid = (props) => {
  const {
    next,
    data,
    keyField,
    hasMore,
    offset = 2,
    rowHeight,
    renderItem,
    dataLength = 0,
    breakPoints,
    numberOfColumns = 1,
    listEmptyComponent = null,
    loader = null
  } = props

  const [items, setItems] = useState([])
  const [padding, setPadding] = useState({ top: 0, bottom: 0 })
  const [columns, setColumns] = useState(1)
  const [itemHeight, setItemHeight] = useState(null)

  const containerRef = useRef(null)
  const previousStartRow = useRef()
  const previousDataLength = useRef()

  const showViewableItems =
    useCallback(() => {
      if (data.length === 0) return

      if (itemHeight === null) return

      const top =
        document.documentElement.scrollTop -
        containerRef.current.offsetTop

      const viewPortHeight =
        document.documentElement.clientHeight

      const totalRows = Math.ceil(data.length / columns)

      let startRow =
        Math.max(((top / itemHeight) | 0), 0)

      if (startRow > totalRows) return

      if (previousStartRow.current === startRow) return
      else previousStartRow.current = startRow

      const numberOfRows = Math.ceil(viewPortHeight / itemHeight)

      let endRow = startRow + numberOfRows + offset

      endRow =
        Math.min(endRow, totalRows)

      startRow =
        Math.max(startRow - offset, 0)

      const result =
        data.slice(startRow * columns, endRow * columns)

      setItems(result)

      setPadding({
        top: startRow * itemHeight,
        bottom: (totalRows - endRow) * itemHeight
      })
    }, [data, columns, itemHeight])

  const listenToScrollProgress =
    useCallback(() => {
      if (!next) return

      if (!hasMore) return

      if (data.length === 0) return

      const winScroll = document.documentElement.scrollTop

      const height =
        document.documentElement.scrollHeight -
        document.documentElement.clientHeight

      if (winScroll + 100 < height) return

      if (previousDataLength.current === dataLength) return

      previousDataLength.current = dataLength

      next()
    }, [
      data,
      next,
      hasMore,
      dataLength
    ])

  const handleResize =
    useCallback(() => {
      previousStartRow.current = undefined

      showViewableItems()

      const breakPoint = responsive(breakPoints)

      const columns =
        breakPoint?.numberOfColumns ||
        numberOfColumns

      const height =
        breakPoint?.rowHeight ||
        rowHeight

      setColumns(columns)

      setItemHeight(height)
    }, [
      data,
      breakPoints,
      dataLength,
      hasMore,
      columns,
      itemHeight
    ])

  const handleScroll =
    useCallback(() => {
      showViewableItems()

      listenToScrollProgress()
    }, [
      data,
      next,
      hasMore,
      dataLength,
      columns,
      itemHeight
    ])

  const initilize = () => {
    const breakPoint = responsive(breakPoints)

    const columns =
      breakPoint?.numberOfColumns ||
      numberOfColumns

    const height =
      breakPoint?.rowHeight ||
      rowHeight

    setColumns(columns)

    setItemHeight(height)
  }

  useLayoutEffect(() => {
    previousStartRow.current = undefined

    showViewableItems()

    setTimeout(() => {
      listenToScrollProgress()
    }, 5)
  }, [
    data,
    columns,
    itemHeight
  ])

  useLayoutEffect(() => {
    window.addEventListener("resize", handleResize)

    window.addEventListener("scroll", handleScroll)

    return () => {
      window.removeEventListener("resize", handleResize)

      window.removeEventListener("scroll", handleScroll)
    }
  }, [
    data,
    columns,
    dataLength,
    hasMore,
    itemHeight
  ])

  useLayoutEffect(() => {
    initilize()
  }, [])

  if (data.length === 0)
    return listEmptyComponent

  return (
    <Fragment>
      <div
        ref={containerRef}
        style={{
          display: "flex",
          flexWrap: "wrap",
          paddingTop: `${padding.top}px`,
          paddingBottom: `${padding.bottom}px`
        }}
      >
        {items.map((item, index) =>
          <div
            key={keyField ? item[keyField] : index}
            style={{
              width: `calc(100% / ${columns})`,
              height: `${itemHeight}px`
            }}
          >
            {renderItem(item, index)}
          </div>
        )}
      </div>
      {hasMore && loader}
    </Fragment>
  )
}

export default WindowVirtualGrid